import React, { useState } from "react";
import { PageHero, Section } from "../components/PageShell";
import { CONTACT_EMAILS, BRAND, OFFICES } from "../data/site";
import { Mail, MapPin, Phone, ArrowRight } from "lucide-react";

const Contact = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        company: "",
        subject: "",
        message: "",
    });

    const onChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = (e) => {
        e.preventDefault();
        const subject = encodeURIComponent(
            form.subject || `Business enquiry — ${form.name || "New contact"}`
        );
        const body = encodeURIComponent(
            `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company}\n\n${form.message}`
        );
        window.location.href = `mailto:${CONTACT_EMAILS[0]}?subject=${subject}&body=${body}`;
    };

    return (
        <>
            <PageHero
                eyebrow="Business Enquiries"
                title="Contact"
                lead="For business enquiries, partnership opportunities, or general information, please use the form below or write directly to our team."
            />

            <Section>
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Form */}
                    <form
                        onSubmit={onSubmit}
                        className="lg:col-span-7 space-y-6"
                        data-testid="contact-form"
                    >
                        <Field
                            id="name"
                            label="Full Name"
                            value={form.name}
                            onChange={onChange}
                            required
                        />
                        <Field
                            id="email"
                            type="email"
                            label="Email Address"
                            value={form.email}
                            onChange={onChange}
                            required
                        />
                        <Field
                            id="company"
                            label="Company Name"
                            value={form.company}
                            onChange={onChange}
                        />
                        <Field
                            id="subject"
                            label="Subject"
                            value={form.subject}
                            onChange={onChange}
                        />

                        <div>
                            <label
                                htmlFor="message"
                                className="block font-body text-[11px] uppercase tracking-[0.22em] mb-3"
                                style={{ color: "var(--ns-muted)" }}
                            >
                                Message
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                value={form.message}
                                onChange={onChange}
                                required
                                rows={6}
                                className="w-full bg-white border border-[var(--ns-border)] px-4 py-3 font-body text-base focus:outline-none focus:border-[var(--ns-ink)] transition-colors"
                                style={{ color: "var(--ns-ink)" }}
                            />
                        </div>

                        <button
                            type="submit"
                            className="ns-btn-primary"
                            data-testid="contact-submit"
                        >
                            Submit Enquiry <ArrowRight size={16} />
                        </button>

                        <p
                            className="font-body text-xs"
                            style={{ color: "var(--ns-muted-2)" }}
                        >
                            Submitting opens your email client with the message
                            pre-filled.
                        </p>
                    </form>

                    {/* Side info */}
                    <aside className="lg:col-span-5">
                        <div
                            className="font-body text-[11px] uppercase tracking-[0.22em] mb-5"
                            style={{ color: "var(--ns-muted)" }}
                        >
                            Email
                        </div>
                        <a
                            href={`mailto:${CONTACT_EMAILS[0]}`}
                            className="group flex items-center gap-3 mb-12"
                            data-testid={`contact-email-${CONTACT_EMAILS[0]}`}
                        >
                            <Mail
                                size={18}
                                style={{ color: "var(--ns-ink)" }}
                            />
                            <span
                                className="font-display text-lg font-medium group-hover:underline"
                                style={{ color: "var(--ns-ink)" }}
                            >
                                {CONTACT_EMAILS[0]}
                            </span>
                        </a>

                        <div className="ns-hr mb-10" />

                        <div
                            className="font-body text-[11px] uppercase tracking-[0.22em] mb-6"
                            style={{ color: "var(--ns-muted)" }}
                        >
                            Offices
                        </div>

                        <div className="space-y-8">
                            {OFFICES.map((o, i) => (
                                <div
                                    key={i}
                                    className="pb-8 border-b border-[var(--ns-border)] last:border-0 last:pb-0"
                                    data-testid={`office-${i}`}
                                >
                                    <div
                                        className="font-body text-[11px] uppercase tracking-[0.22em] mb-2"
                                        style={{ color: "var(--ns-muted-2)" }}
                                    >
                                        {o.label}
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <MapPin
                                            size={16}
                                            className="mt-1 shrink-0"
                                            style={{ color: "var(--ns-ink)" }}
                                        />
                                        <div>
                                            <div
                                                className="font-display text-base font-semibold"
                                                style={{ color: "var(--ns-ink)" }}
                                            >
                                                {o.city}
                                            </div>
                                            <div
                                                className="font-body text-sm mt-1"
                                                style={{ color: "var(--ns-body)" }}
                                            >
                                                {o.address}
                                            </div>
                                        </div>
                                    </div>

                                    {o.phones && (
                                        <div className="mt-4 ml-7 space-y-1.5">
                                            {o.phones.map((p, j) => (
                                                <div
                                                    key={j}
                                                    className="flex items-center gap-2.5"
                                                >
                                                    <Phone
                                                        size={13}
                                                        style={{ color: "var(--ns-muted)" }}
                                                    />
                                                    <span
                                                        className="font-body text-xs uppercase tracking-[0.15em]"
                                                        style={{ color: "var(--ns-muted)" }}
                                                    >
                                                        {p.kind}
                                                    </span>
                                                    <a
                                                        href={`tel:${p.number.replace(/[^\d+]/g, "")}`}
                                                        className="font-body text-sm tabular-nums hover:underline"
                                                        style={{ color: "var(--ns-ink)" }}
                                                    >
                                                        {p.number}
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="ns-hr my-10" />

                        <div>
                            <div
                                className="font-body text-[11px] uppercase tracking-[0.22em] mb-3"
                                style={{ color: "var(--ns-muted)" }}
                            >
                                Legal entity
                            </div>
                            <p
                                className="font-body text-base"
                                style={{ color: "var(--ns-ink)" }}
                            >
                                {BRAND.legal}
                            </p>
                        </div>
                    </aside>
                </div>
            </Section>
        </>
    );
};

const Field = ({ id, label, type = "text", value, onChange, required }) => (
    <div>
        <label
            htmlFor={id}
            className="block font-body text-[11px] uppercase tracking-[0.22em] mb-3"
            style={{ color: "var(--ns-muted)" }}
        >
            {label}
            {required && <span style={{ color: "var(--ns-ink)" }}> *</span>}
        </label>
        <input
            id={id}
            name={id}
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            className="w-full bg-white border border-[var(--ns-border)] px-4 py-3 font-body text-base focus:outline-none focus:border-[var(--ns-ink)] transition-colors"
            style={{ color: "var(--ns-ink)" }}
        />
    </div>
);

export default Contact;
